import React, { useId } from "react"

import * as Headless from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/16/solid"
import clsx from "clsx"

import { CONTROL_HEIGHT_CLASS } from "./control"
import { Description, ErrorMessage, Field, Label } from "./fieldset"

export type SelectFieldProps<T> = {
  label?: React.ReactNode
  labelSrOnly?: boolean
  description?: React.ReactNode
  error?: React.ReactNode
  disabled?: boolean
  className?: string
  placeholder?: string
  name?: string
  options: T[]
  value: T | null
  onChange: (value: T) => void
  getKey: (option: T) => string | number
  getLabel: (option: T) => string
  renderOption?: (option: T) => React.ReactNode
  /** Options shown but not selectable. */
  isOptionDisabled?: (option: T) => boolean
  portal?: boolean
}

const IS_DEV_BUILD = process.env.NODE_ENV !== `production`

export function SelectField<T>({
  label,
  labelSrOnly = false,
  description,
  error,
  disabled,
  className,
  placeholder,
  name,
  options,
  value,
  onChange,
  getKey,
  getLabel,
  renderOption,
  isOptionDisabled,
  portal = true,
}: SelectFieldProps<T>) {
  const errorId = useId()
  const errorMessageId = error ? errorId : undefined

  if (IS_DEV_BUILD && !label && !labelSrOnly) {
    // eslint-disable-next-line no-console
    console.error(
      `<SelectField> requires a 'label' prop, or 'labelSrOnly' must be true to acknowledge the field is intentionally unlabelled.`,
    )
  }

  return (
    <Field className={className} disabled={disabled}>
      {label ? (
        <Label
          className={labelSrOnly ? `sr-only` : `text-sm font-medium text-muted`}
        >
          {label}
        </Label>
      ) : null}
      {description ? <Description>{description}</Description> : null}
      <Headless.Listbox
        as="div"
        data-slot="control"
        // Pass null through: Headless UI treats only undefined as uncontrolled,
        // so a cleared field must stay null to remain controlled.
        value={value}
        onChange={(next: T | null) => {
          if (next !== null) onChange(next)
        }}
        disabled={disabled}
        name={name}
        multiple={false}
      >
        <span
          className={clsx(
            `relative block w-full`,
            // The disabled button, not a disabled option rendered inline (portal={false}).
            `has-[>[data-disabled]]:cursor-not-allowed has-[>[data-disabled]]:opacity-50`,
          )}
        >
          <Headless.ListboxButton
            aria-invalid={error ? true : undefined}
            aria-errormessage={errorMessageId}
            data-invalid={error ? true : undefined}
            className={clsx(
              CONTROL_HEIGHT_CLASS,
              `group relative flex w-full appearance-none items-center justify-between rounded-md`,
              `border border-control-border bg-background px-2.5 text-left shadow-sm`,
              `text-base/6 text-muted sm:text-sm/6`,
              `data-hover:border-primary`,
              `focus:outline-hidden`,
              `data-focus:border-primary data-focus:ring-2 data-focus:ring-focus-ring/25`,
              `data-invalid:border-danger data-hover:data-invalid:border-danger data-focus:data-invalid:ring-danger/20`,
              `data-disabled:cursor-not-allowed data-disabled:border-control-border data-disabled:bg-surface data-hover:data-disabled:border-control-border`,
            )}
          >
            <span
              className={clsx(
                `block min-w-0 flex-1 truncate text-[var(--step--1)]`,
                value === null ? `text-muted` : `text-foreground`,
              )}
            >
              {value === null ? (placeholder ?? `Select…`) : getLabel(value)}
            </span>
            <span
              aria-hidden="true"
              className="ml-2 flex h-1/2 shrink-0 items-center border-l border-border pl-2.5 text-muted"
            >
              <ChevronDownIcon className="size-4 transition-transform group-data-open:rotate-180" />
            </span>
          </Headless.ListboxButton>

          <Headless.ListboxOptions
            transition
            portal={portal}
            modal={portal}
            {...(portal ? { anchor: `bottom` as const } : {})}
            className={clsx(
              portal
                ? `[--anchor-gap:10px] [--anchor-padding:--spacing(2)] isolate z-[var(--z-listbox)] w-[var(--button-width)] min-w-[var(--button-width)]`
                : `absolute inset-x-0 top-full z-[var(--z-listbox)] mt-1.5 w-full`,
              `max-h-[302px] overflow-auto rounded select-none`,
              `outline outline-transparent focus:outline-hidden`,
              `border border-primary bg-background shadow-lg`,
              `transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none`,
            )}
          >
            {options.map((option) => (
              <Headless.ListboxOption
                key={getKey(option)}
                value={option}
                disabled={isOptionDisabled?.(option) ?? false}
                className={clsx(
                  `group/option relative flex cursor-pointer items-center gap-3`,
                  `border-t border-primary/20 px-[15px] py-[15px] first:border-t-0`,
                  `text-base/6 text-foreground sm:text-sm/6`,
                  `outline-hidden select-none`,
                  `data-focus:bg-primary data-focus:text-primary-foreground`,
                  `data-disabled:opacity-50`,
                )}
              >
                <span className="block min-w-0 flex-1 truncate font-normal capitalize group-data-selected/option:font-semibold">
                  {renderOption ? renderOption(option) : getLabel(option)}
                </span>
                <span
                  className={clsx(
                    `flex size-4 shrink-0 items-center justify-center rounded-full border border-control-border bg-background`,
                    `group-data-selected/option:border-primary group-data-selected/option:ring-1 group-data-selected/option:ring-primary`,
                    `group-data-focus/option:border-primary-foreground group-data-focus/option:bg-primary-foreground group-data-focus/option:ring-primary-foreground`,
                  )}
                >
                  <span className="size-2 rounded-full bg-primary opacity-0 group-data-selected/option:opacity-100" />
                </span>
              </Headless.ListboxOption>
            ))}
          </Headless.ListboxOptions>
        </span>
      </Headless.Listbox>
      {error ? <ErrorMessage id={errorMessageId}>{error}</ErrorMessage> : null}
    </Field>
  )
}
